import React, { useCallback, useEffect, useState } from "react";
import {
  fetchCatalogue,
  updateUserLicense,
  fetchAllUpcomingInvoices,
  fetchCatalogueForNewSubscription,
  AddUser,
} from "../services/subscriptionServices";
import { useLoading } from "@/contexts/LoadingContext";
import cardIcon from "@/assets/myaccount/group.svg";
import { Dot } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
// import { Button } from "@/components/ui/button";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
// import { Button } from "jodit/esm/modules";
import { Button } from "@/components/ui/button";
import noOrders from "@/assets/myaccount/no-plans.svg";
import warning from "@/assets/myaccount/warning.svg";
import { createCustomerPortalSession } from "@/features/plans-and-subscription/subscriptionSlice";
// import Loading from "@/routes/loading";
import infoIcon from "@/assets/newHome/info.svg";
import blackInfoIcon from "@/assets/myaccount/blackInfoIcon.svg";

import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

import { toast } from "react-toastify";
import { RxCross2 } from "react-icons/rx";

interface CatalogueItem {
  price_id: string;
  object: string;
  interval: string;
  intervalCount: number;
  product_id: string;
  price: number;
  currency: string;
  name: string;
  quantity: number;
  marketing_features?: string[];
}
type InvoiceItem = {
  description: string;
  quantity: number;
  unitAmount: string;
  amount: string;
  currency: string;
  interval: string;
  intervalCount: number;
  period: {
    start: number;
    end: number;
  };
};

interface UpcomingInvoice {
  amountDue: string;
  itemCount: number;
  items: InvoiceItem[];
  nextPaymentAttempt: number;
  subTotal: string;
  tax: string;
  total: string;
  totalExcludingTax: string;
  period: {
    start: number;
    end: number;
  };
}

type SubscriptionItem = {
  productName: string;
  quantity: number;
  unitAmount: string;
  intervalCount: number;
  amount: string;
  interval: string; // e.g., "month"
  interval_count: number;
  currency: string; // e.g., "cad"
};

type CurrentSubscription = {
  currentPeriodEnd: number; // Unix timestamp
  items: SubscriptionItem[];
  totalAmount: string;
};

interface Price {
  currency: string;
  price: number;
  price_id: string;
  name: string;
  quantity: number;
  interval: string;
  intervalCount: number;
}

interface Group {
  interval: string;
  intervalCount: number;
  title: string;
  prices: Price[];
}

// interface Props {
//   activeTab: string;
// }

const Subscription = () => {
  const { setLoading } = useLoading();
  const [catalogueLicences, setCatalogueLicences] = useState<CatalogueItem[]>(
    []
  );
  const [upcomingInvoice, setUpcomingInvoice] =
    useState<UpcomingInvoice | null>(null);
  const [currentPlans, setCurrentPlans] = useState<CurrentSubscription | null>(
    null
  );
  const [allInvoiceItems, setAllInvoiceItems] = useState<InvoiceItem[]>([]);
  const [subscriptionId, setSubscriptionId] = useState<string>("");
  const [purchasedLicenseCount, setPurchasedLicenseCount] = useState<number>();
  const [updatedLicenses, setUpdatedLicenses] = useState<
    { price_id: string; quantity: number }[]
  >([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddSubDialogOpen, setIsAddSubDialogOpen] = useState(false);
  const [isHoverDialogOpen, setIsHoverDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState<any[]>([]);

  const [purchasedLicensesOverview, setpurchasedLicensesOverview] = useState<
    any[]
  >([]);
  const [currentSubscriptionIndex, setCurrentSubscriptionIndex] = useState(0);
  const [groupedCatalogue, setGroupedCatalogue] = useState<Group[]>([]);

  const fetchPlansCatalogue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchCatalogue();
      const data = response?.data ?? {};

      setActiveSubscriptions(data.activeSubscriptions ?? []);
      setpurchasedLicensesOverview(data.purchasedLicensesOverview ?? []);
      setPurchasedLicenseCount(data.purchasedLicenseCount ?? 0);
    } catch (err) {
      console.error("Error fetching catalogue items:", err);
      setCatalogueLicences([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    if (activeSubscriptions.length > 0) {
      const current = activeSubscriptions[currentSubscriptionIndex];
      setCatalogueLicences(current.catalogue ?? []);
      setUpcomingInvoice(current.upcomingInvoice ?? null);
      setCurrentPlans(current ?? []);
      setSubscriptionId(activeSubscriptions[currentSubscriptionIndex].id);
    }
  }, [activeSubscriptions, currentSubscriptionIndex]);

  useEffect(() => {
    fetchPlansCatalogue();
  }, [fetchPlansCatalogue]);

  const [showAllInvoices, setShowAllInvoices] = useState(false);

  const handleFetchInvoices = async () => {
    setLoading(true);
    const data = await fetchAllUpcomingInvoices();
    setAllInvoiceItems(data);
    setShowAllInvoices(true);
    setLoading(false);
  };

  //  Handle Quantity Changes (Including Zero)
  const handleQuantityChange = (price_id: string, newQuantity: number) => {
    setUpdatedLicenses((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.price_id === price_id
      );

      if (existingIndex > -1) {
        const updatedList = [...prev];
        updatedList[existingIndex].quantity = newQuantity;
        return updatedList;
      }

      return [...prev, { price_id, quantity: newQuantity }];
    });
  };

  const getDisplayQuantity = (price_id: string) => {
    const updatedItem = updatedLicenses.find((u) => u.price_id === price_id);
    return updatedItem !== undefined
      ? updatedItem.quantity
      : catalogueLicences.find((item) => item.price_id === price_id)
          ?.quantity || 0;
  };

  const getFinalPayload = () => {
    return catalogueLicences
      .map((item) => {
        const updatedItem = updatedLicenses.find(
          (u) => u.price_id === item.price_id
        );
        return {
          price_id: item.price_id,
          quantity:
            updatedItem !== undefined ? updatedItem.quantity : item.quantity,
          // Use updated or API quantity
        };
      })
      .filter((item) => item.quantity > 0); // Exclude zero quantities
  };

  const tabs = groupedCatalogue.map((group) => ({
    key: `${group.intervalCount}_${group.interval}`,
    label: group.title,
  }));

  const groupedCatalogueMap = groupedCatalogue.reduce(
    (acc, group) => {
      const key = `${group.intervalCount}_${group.interval}`;
      acc[key] = group;
      return acc;
    },
    {} as Record<string, (typeof groupedCatalogue)[0]>
  );

  const [activeTab, setActiveTab] = useState("1_month");

  //  API Call - Includes Unchanged Quantities & Excludes Zero
  const updateSubUserLicense = async (subscriptionId: string) => {
    const finalPayload = getFinalPayload();

    if (finalPayload.length === 0) return; // Avoid unnecessary API calls if empty
    setIsLoading(true);
    try {
      console.log(finalPayload, "Payload to be sent"); // Debugging
      await updateUserLicense({
        subscriptionId,
        subUserLicenses: finalPayload,
      });
      setUpdatedLicenses([]); // Reset after updating
      fetchPlansCatalogue();
    } catch (err) {
      console.error("Error while updating the sub-user license", err);
    } finally {
      setIsLoading(false);
      console.log("Subscription cancelled");
      setIsDialogOpen(false);
    }
  };

  const timestamp = upcomingInvoice?.nextPaymentAttempt;
  const formateDate = timestamp
    ? new Date(timestamp * 1000).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })
    : "N/A";

  // upcoming invoice's Period

  const sortByStartDate = (items: InvoiceItem[]) => {
    return [...items].sort(
      (a, b) => Number(a.period.start) - Number(b.period.start)
    );
  };

  const formatPeriod = (start: number, end: number) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    };
    const formatDate = (timestamp: number) =>
      // new Date(timestamp *1000).toUTCString();
      new Date(timestamp * 1000).toLocaleDateString("en-US", options);

    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const groupByPeriod = (items: InvoiceItem[]) => {
    const groups: Record<string, InvoiceItem[]> = {};
    items.forEach((item) => {
      const period = formatPeriod(item.period.start, item.period.end);
      console.log(item.period.start, item.period.end);
      if (!groups[period]) {
        groups[period] = [];
      }
      groups[period].push(item);
    });
    return groups;
  };

  const setUpCustomerPortalSession = useCallback(async () => {
    setLoading(true);
    const res = await createCustomerPortalSession({
      returnUrl: "https://buildmapper.ai",
    });
    setLoading(false);
    if (res.statusCode == 200) {
      const session = res.data.customerPortalSession;
      if (session) {
        window.open(session.url, "_blank");
      }
    }
  }, [setLoading]);

  const sortedItems = sortByStartDate(
    showAllInvoices ? allInvoiceItems : upcomingInvoice?.items || []
  );

  const groupedItems = groupByPeriod(sortedItems);

  // // 3. Extract keys and current group
  // const groupKeys = Object.keys(groupedItems);
  // const currentGroupKey = groupKeys[currentGroupIndex];
  // const currentGroupItems = groupedItems[currentGroupKey] || [];

  const handlePreviousGroup = () => {
    setCurrentSubscriptionIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextGroup = () => {
    setCurrentSubscriptionIndex((prev) =>
      prev < activeSubscriptions.length - 1 ? prev + 1 : prev
    );
  };

  useEffect(() => {
    const loadCatalogue = async () => {
      setLoading(true);
      try {
        const res = await fetchCatalogueForNewSubscription();
        setGroupedCatalogue(res?.data?.groupedCatalogue || []);
      } catch (error) {
        console.error("Failed to fetch catalogue", error);
      } finally {
        setLoading(false);
      }
    };

    loadCatalogue();
  }, []);

  const onSubmit = async () => {
    const selectedPlans = Object.entries(quantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([price_id, quantity]) => ({ price_id, quantity }));

    if (selectedPlans.length === 0) {
      alert("Please select at least one subscription.");
      return;
    }

    const payload = { newSubUserLicenses: selectedPlans };

    try {
      const result = await AddUser(payload);
      console.log("API Response:", result);
      setIsAddSubDialogOpen(false);
      toast.success("Subscription Added Successfully!");
      fetchPlansCatalogue();

      setQuantities({});
    } catch (error) {
      console.error("Error submitting subscriptions:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  const calculateSubtotal = () => {
    const prices = groupedCatalogueMap[activeTab]?.prices || [];
    return prices.reduce((total, price) => {
      const qty = quantities[price.price_id] || 0;
      return total + qty * price.price;
    }, 0);
  };

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const handleQuantityChange1 = (priceId: string, newQuantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [priceId]: Math.max(0, newQuantity),
    }));
  };

  return (
    <div className="h-full p-4 mt-0">
      {activeSubscriptions.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-[15%] px-4 space-y-6">
          <img src={noOrders} alt="No Users" className="w-55 h-40 " />

          <p className="text-[#202b49] text-[20px] ">
            Loading your subscription plans...
          </p>
        </div>
      ) : (
        <div className="h-full w-[95%] mx-auto  mt-0 ">
          <div className="mt-8">
            <div className="flex justify-between items-start ">
              <div className="flex space-x-6 items-center">
                <h2 className="text-[28px] font-outfit font-medium">
                  Subscription{" "}
                  <span className="text-[#28af4a]">
                    {currentSubscriptionIndex + 1}/{activeSubscriptions.length}
                  </span>
                </h2>

                <div className="flex space-x-3 ">
                  <FaChevronLeft
                    size={30}
                    onClick={
                      currentSubscriptionIndex === 0
                        ? undefined
                        : handlePreviousGroup
                    }
                    className={`cursor-pointer ${
                      currentSubscriptionIndex === 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-green-600"
                    }`}
                  />
                  <FaChevronRight
                    size={30}
                    onClick={
                      currentSubscriptionIndex + 1 ===
                      activeSubscriptions.length
                        ? undefined
                        : handleNextGroup
                    }
                    className={`cursor-pointer ${
                      currentSubscriptionIndex + 1 ===
                      activeSubscriptions.length
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-green-600"
                    }`}
                  />
                </div>
              </div>

              <div className="flex space-x-4 items-center ">
                <div className="flex space-x-1 ">
                  {typeof purchasedLicenseCount === "number" && (
                    <h2 className="text-[14px] font-medium">
                      Total Purchased Licenses:
                      <span className="font-semibold">
                        {" "}
                        {purchasedLicenseCount}{" "}
                      </span>
                    </h2>
                  )}
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <img
                        src={blackInfoIcon}
                        className="size-[20px] align-text-top  hover:cursor-pointer"
                      ></img>
                    </HoverCardTrigger>
                    <HoverCardContent className="min-w-[520px] max-w-[600px] w-auto p-0 rounded-md mt-1.5 rounded-t-md pb-2">
                      <div className="flex justify-between items-center border-b pb-2 bg-[#f3f4f6] pt-2 w-full">
                        <h2 className="text-[15px] font-semibold text-gray-800 pl-3">
                          Subscription Plans
                        </h2>
                        <h2 className="text-[15px] font-semibold text-gray-800 pr-3 ml-2">
                          Quantity
                        </h2>
                      </div>

                      <div className="space-y-2 px-3  pt-2">
                        {(purchasedLicensesOverview?.slice(0, 4) || []).map(
                          (feature, index) => (
                            <div key={index} className="flex justify-between  ">
                              <p className="text-[14px]  text-gray-900 w-[80%]">
                                <div className="flex items-center text-[14px] leading-[24px]">
                                  {feature.product_name.length > 30
                                    ? `${feature.product_name.slice(0, 30)}...`
                                    : feature.product_name}
                                  <Dot size={30} />
                                  <div className="text-[#828282] ">
                                    <span className=" ">$</span>
                                    <span className=" leading-[1.3] ml-1">
                                      {feature.amount}{" "}
                                    </span>
                                    <span className=" leading-[1.3] uppercase ">
                                      {feature.currency}{" "}
                                    </span>
                                    <span className="text-[14px] ">
                                      / {feature.intervalCount}{" "}
                                      {feature.intervalCount > 1
                                        ? `${feature.interval}s`
                                        : `${feature.interval}`}
                                    </span>
                                  </div>
                                </div>
                              </p>
                              <p className="text-[14px] font-medium text-start w-[10%] text-gray-700">
                                {feature.quantity}
                              </p>
                            </div>
                          )
                        )}

                        {/* Show "View all plans" if more than 4 items */}
                        {purchasedLicensesOverview &&
                          purchasedLicensesOverview.length > 4 && (
                            <div
                              className="pt-1 text-[#28af4a] text-sm font-medium cursor-pointer hover:underline"
                              onClick={() => setIsHoverDialogOpen(true)}
                            >
                              View all plans
                            </div>
                          )}
                      </div>
                    </HoverCardContent>

                    {isHoverDialogOpen && (
                      <div
                        className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50"
                        onClick={() => setIsHoverDialogOpen(false)}
                      >
                        <div
                          className="bg-white rounded-lg max-w-2xl w-full shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Header */}
                          <div className="bg-[#f4f4f4] flex justify-between items-center px-6 py-4 rounded-t-lg">
                            <h3 className="text-[20px] font-medium text-[#202b49]">
                              Total Purchased Licenses
                            </h3>
                            <RxCross2
                              size={20}
                              className="hover:cursor-pointer"
                              onClick={() => setIsHoverDialogOpen(false)}
                            />
                          </div>

                          <div className="overflow-x-auto max-h-80 overflow-y-auto p-6">
                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                              <table className="min-w-full text-sm border-separate border-spacing-0">
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {purchasedLicensesOverview.map(
                                    (feature, index) => (
                                      <tr key={index}>
                                        <td className="px-4 py-3 text-gray-900 border-b w-[80%] border-gray-200">
                                          <div className="flex items-center text-[14px] leading-[24px]">
                                            {feature.product_name.length > 30
                                              ? `${feature.product_name.slice(0, 30)}...`
                                              : feature.product_name}
                                            <Dot size={30} />
                                            <div className="text-[#828282] ">
                                              <span className=" ">$</span>
                                              <span className=" leading-[1.3] ml-1">
                                                {feature.amount}{" "}
                                              </span>
                                              <span className=" leading-[1.3] uppercase ">
                                                {feature.currency}{" "}
                                              </span>
                                              <span className="text-[16px] ">
                                                / {feature.intervalCount}{" "}
                                                {feature.intervalCount > 1
                                                  ? `${feature.interval}s`
                                                  : `${feature.interval}`}
                                              </span>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 border-b border-gray-200">
                                          {feature.quantity}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </HoverCard>
                </div>
                <Button
                  onClick={setUpCustomerPortalSession}
                  className="bg-charcoal hover:bg-charcoal/90 text-white px-3 py-1 text-sm h-9"
                >
                  <span className="px-2 p-2">Manage Subscription</span>
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <img src={infoIcon} className="size-[16px] "></img>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-50 mt-2" align="end">
                      <div className="flex  space-x-4">
                        <div className="space-y-0.5">
                          {/* <h4 className="text-[20px] text-primary uppercase font-semibold">{item.name}</h4> */}
                          <p className="text-[14px] text-[#181818]/70">
                            Click here to cancel your subscription or view your
                            invoice history.
                          </p>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </Button>

                <Button
                  className="bg-[#181818] hover:bg-charcoal/90 text-white px-3 py-1 text-sm h-9"
                  onClick={() => setIsAddSubDialogOpen(true)}
                >
                  <span className="px-2 p-2">Add New Subscription</span>
                </Button>
              </div>
            </div>
            <hr className="border-t border-gray-300 mt-4" />
          </div>

          {/* Header Section */}
          <div className="flex justify-between items-start mt-6">
            {/* Left Section */}
            <div className="flex j">
              {catalogueLicences.length > 0 && (
                <h2 className="text-[18px] font-outfit">Current Plan</h2>
              )}
            </div>

            {/* Right Section */}
            {/* <Button className="bg-charcoal hover:bg-charcoal/90 text-white px-3 py-1 text-sm h-9">
  Manage Subscription
</Button> */}
            <div className="relative flex items-center gap-5">
              {updatedLicenses.length > 0 && (
                <button
                  className="bg-primary text-white px-6 py-[7px] flex justify-center rounded-lg shadow-lg hover:bg-green-700 transition"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Update
                </button>
              )}
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4  2xl:grid-cols-5 gap-5 mt-6 ">
            {catalogueLicences.length > 0 &&
              catalogueLicences.map((item) => {
                const selectorQuantity = getDisplayQuantity(item.price_id);
                // const subUserLicenseQuantity = item.quantity; // Always from API

                return (
                  <div
                    key={item.price_id}
                    className="p-4 border border-[#e7e9ed] bg-white rounded-[10px] flex flex-col justify-between"
                  >
                    {/* Plan Name */}
                    <div className="flex my-auto gap-x-1">
                      <h3 className="text-[12px] uppercase font-semibold tracking-[1px] text-[#28af4a]">
                        {item.name}
                      </h3>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <img
                            src={cardIcon}
                            className="size-[16px] align-text-top mt-[3px]"
                          ></img>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 ">
                          <div className="flex  space-x-4">
                            <div className="space-y-1">
                              {/* <h4 className="text-[20px] text-primary uppercase font-semibold">{item.name}</h4> */}
                              {/* <p className="text-[14px] text-[#181818]/70">
                            Handle your entire sales paperwork process efficiently and professionally.
                          </p> */}
                              {item.marketing_features?.map(
                                (feature, index) => (
                                  <div
                                    key={index}
                                    className="flex flex-col pt-4"
                                  >
                                    <h2 className="text-[17px] font-semibold text-[#161c2d]">
                                      {feature.split(":")[0]}
                                    </h2>
                                    <p className="text-[14px] font-medium text-[#181818]/70">
                                      {feature.split(":")[1]}
                                    </p>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>

                    <div></div>

                    {/* Price Section */}
                    <div className="flex items-center">
                      <span className="text-[24px] font-bold mt-2 text-[#161c2d]">
                        $
                      </span>
                      <span className="text-[40px] font-bold leading-[1.3] text-[#161c2d] ">
                        {item.price}
                      </span>
                      <span className="text-[16px] text-[#828282] ml-1 mt-2">
                        / {item.intervalCount}{" "}
                        {item.intervalCount > 1
                          ? `${item.interval}s`
                          : item.interval}
                      </span>
                    </div>

                    {/* 🔹 Sub-User License (ALWAYS from API)
                <span className="text-[16px] text-[#161c2d] mt-2">
                  Sub-User Licence: {subUserLicenseQuantity}
                </span> */}

                    {/* 🔹 Quantity Selector (Can Show 0) */}
                    <div className="flex items-center  h-[48px] mt-3 mx-[1px] border border-[#e4e4e4] bg-white rounded-[8px]">
                      <button
                        className="p-6 h-full flex items-center justify-center text-2xl font-bold  bg-[#828282] bg-opacity-[0.08]"
                        onClick={() =>
                          handleQuantityChange(
                            item.price_id,
                            Math.max(0, selectorQuantity - 1)
                          )
                        }
                      >
                        -
                      </button>
                      <span className="text-lg font-medium flex-1 text-center text-gray-800">
                        {selectorQuantity}
                      </span>
                      <button
                        className="p-6 h-full flex items-center justify-center text-2xl font-bold bg-[#828282] bg-opacity-[0.08]"
                        onClick={() =>
                          handleQuantityChange(
                            item.price_id,
                            selectorQuantity + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Update Button - Only Show When There's a Change */}

          {/* Pricing Table */}

          <div className="mt-5">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-medium ">Pricing</h2>
            </div>

            <div className="border border-1 rounded-sm mt-3 ">
              {currentPlans?.items?.length === 0 ? (
                <div className="flex justify-center items-center ">
                  <p className="text-gray-500 text-lg">No Users Exist</p>
                </div>
              ) : (
                <Table className="">
                  <TableHeader className="bg-[#f9fafb]">
                    <TableRow>
                      <TableHead className="p-4 text-black font-medium text-[16px] text-left 2xl:w-[70%] w-[65%] ">
                        Product
                      </TableHead>
                      <TableHead className="p-2 text-black font-medium text-[16px] text-left 2xl:w-[10%] w-[12%] ">
                        Qty
                      </TableHead>
                      <TableHead className="p-2 text-black font-medium text-[16px]  text-left 2xl:w-[20%] w-[27%]">
                        Total
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {(currentPlans?.items ?? []).map((item, itemIndex) => (
                      <TableRow key={`item-${itemIndex}`}>
                        <TableCell className="p-4 text-left  flex flex-row 2xl:w-[70%] w-[60%] items-center">
                          {item.productName}
                          <Dot size={30} />
                          <div className="text-[#828282] ">
                            <span className=" ">$</span>
                            <span className=" leading-[1.3] ml-1">
                              {item.unitAmount}{" "}
                            </span>
                            <span className=" leading-[1.3] uppercase ">
                              {item.currency}{" "}
                            </span>
                            <span className="text-[16px] ">
                              /{item.intervalCount}{" "}
                              {item.intervalCount > 1
                                ? `${item.interval}s`
                                : item.interval}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell className="p-4 text-left ">
                          {item.quantity}
                        </TableCell>

                        <TableCell className="p-4  text-left">
                          <span className=" text-[#161c2d]">$</span>
                          <span className=" leading-[1.3] ml-1">
                            {item.amount}{" "}
                          </span>
                          <span className=" leading-[1.3] uppercase ">
                            {item.currency}{" "}
                          </span>
                          <span className="text-[16px] ">
                            /{item.intervalCount}{" "}
                            {item.intervalCount > 1
                              ? `${item.interval}s`
                              : item.interval}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell className="2xl:pl-4 flex space-x-7">
                        <h2 className="text-[16px]">Total Amount</h2>
                        <div>
                          <span className="text-[#161c2d]">$</span>
                          <span> {currentPlans?.totalAmount}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              )}
            </div>
          </div>

          {/* Upcoming Invoice */}
          {upcomingInvoice && (
            <div className="my-8">
              <h2 className="text-xl font-medium ">Upcoming Invoice</h2>
              <p className="text-[#828282] text-[16px] my-2">
                This is a preview of the invoice that will be billed on{" "}
                <span className="text-primary">{formateDate}</span>. It may
                change if the subscription is updated.
              </p>
              <div className="border border-t rounded-sm mt-3 ">
                {upcomingInvoice?.items?.length === 0 ? (
                  <div className="flex justify-center items-center ">
                    <p className="text-gray-500 text-lg">No Users Exist</p>
                  </div>
                ) : (
                  <Table className="">
                    <TableHeader className="bg-[#f9fafb]">
                      <TableRow>
                        <TableHead className="p-4 text-black font-medium text-[16px] 2xl:w-[70%] w-[65%]  text-left ">
                          Description
                        </TableHead>
                        <TableHead className="p-4  text-black font-medium text-[16px] 2xl:w-[10%] w-[12%] text-left ">
                          Qty
                        </TableHead>
                        {/* <TableHead className="p-2 text-black font-medium text-[16px]  pl-9 text-left">
                      Unit Price
                    </TableHead> */}
                        <TableHead className="p-4 text-black text-[16px] font-medium 2xl:w-[20%] w-[27%] text-left">
                          Amount
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {Object.entries(groupedItems).map(([period, items]) => (
                        <React.Fragment key={period}>
                          <TableRow className="bg-[#ffffff]">
                            <TableCell className="p-4  text-[#828282] font-bold">
                              {period}
                            </TableCell>
                          </TableRow>
                          {items.map((item, index) => (
                            <TableRow
                              key={`item-${index}`}
                              className="border-b"
                            >
                              <TableCell className="p-4 text-left  ">
                                {item.description}
                              </TableCell>

                              <TableCell className="p-4  text-left ">
                                {item.quantity}
                              </TableCell>

                              {/* <TableCell className="p-4 pl-10 text-left">
                            <span className="text-[#161c2d]">$</span>
                            <span className="leading-[1.3] text-[#161c2d] ml-1">
                              {item.unitAmount}
                            </span>
                          </TableCell> */}

                              <TableCell className="p-4  text-left">
                                <span className="text-[#161c2d]">$</span>
                                <span className="leading-[1.3] text-[#161c2d] ml-1">
                                  {item.amount}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>

                    {/* Initially show View all and after clicking on view all , show view less */}

                    <TableFooter className="bg-[#ffffff] ">
                      {allInvoiceItems.length === 0 &&
                        (upcomingInvoice?.itemCount ?? 0) > 10 && (
                          <TableRow>
                            <TableCell
                              onClick={handleFetchInvoices}
                              className="p-4 font-medium text-[16px] text-primary hover:cursor-pointer"
                            >
                              View all <span>{upcomingInvoice?.itemCount}</span>{" "}
                              items
                            </TableCell>
                          </TableRow>
                        )}
                    </TableFooter>
                  </Table>
                )}

                {upcomingInvoice && (
                  <div>
                    <div className=" border-t flex  flex-col justify-end items-end  py-6 gap-y-4">
                      <div className="grid grid-cols-2 gap-y-4  2xl:w-[37%] w-[45%]">
                        {/* Subtotal */}
                        <div className="text-left ">Subtotal</div>
                        <div className="text-left ">
                          <span className="text-[#161c2d]">$</span>
                          <span className="leading-[1.3] text-[#161c2d]">
                            {upcomingInvoice.subTotal}
                          </span>
                        </div>

                        {/* Total Excluding Tax */}
                        <div className="text-left ">Total excluding tax</div>
                        <div className="text-left ">
                          <span className="text-[#161c2d]">$</span>
                          <span className="leading-[1.3] text-[#161c2d]">
                            {upcomingInvoice.totalExcludingTax}
                          </span>
                        </div>

                        {/* Tax */}
                        <div className="text-left ">Tax</div>
                        <div className="text-left ">
                          <span className="text-[#161c2d]">$</span>
                          <span className="leading-[1.3] text-[#161c2d]">
                            {upcomingInvoice.tax}
                          </span>
                        </div>

                        {/* Total */}
                        <div className="text-left font-semibold ">Total</div>
                        <div className="text-left font-semibold ">
                          <span className="text-[#161c2d]">$</span>
                          <span className="leading-[1.3] text-[#161c2d]">
                            {upcomingInvoice.total}
                          </span>
                        </div>

                        {/* Amount Due */}
                      </div>
                    </div>

                    <div className=" border-t flex p-4 flex-col justify-end items-end  gap-y-4">
                      <div className="grid grid-cols-2  2xl:w-[37%] w-[45%]">
                        <div className="text-left font-medium ">Amount Due</div>
                        <div className="text-left font-medium pl-2 ">
                          <span className="text-[#161c2d]">$</span>
                          <span className="leading-[1.3] text-[#161c2d]">
                            {upcomingInvoice.amountDue}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* <div className=" border-t flex p-4  justify-start items-start pl-10 ">
                  <span className="text-primary font-medium text-[16px] ">View full invoice for <span>{upcomingInvoice.amountDue}</span> on <span>{formateDate}</span></span>
                </div> */}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Update Button - Only Show When There's a Change */}
          {/* {updatedLicenses.length > 0 && (
        <button
          className="fixed bottom-8 left-1/2 bg-primary text-white px-6 py-3 rounded-lg shadow-lg hover:bg-green-700 transition"
          onClick={updateSubUserLicense}
        >
          Update
        </button>
      )} */}
          <Dialog
            open={isDialogOpen}
            onOpenChange={(val) => !isLoading && setIsDialogOpen(val)}
          >
            <DialogContent className="w-[30%] max-w-[900px] p-0 flex flex-col  rounded-xl overflow-hidden bg-white">
              <DialogHeader className="bg-[#f4f4f4] text-[#181818] text-[22px] px-6 py-4 ">
                <DialogTitle>Update Subscription</DialogTitle>
              </DialogHeader>
              <DialogDescription>
                <div className="flex flex-col items-center justify-center text-center px-6 py-4 space-y-4">
                  <img src={warning} alt="Warning" className="w-20 h-20" />
                  <p className="text-[#363636] text-[16px] leading-[24px] ">
                    You are about to update this subscription plan.
                    <br />
                    Are you sure you want to proceed?
                  </p>
                </div>
              </DialogDescription>

              <DialogFooter className="flex border-t  border-gray-200 p-3 ">
                <div className="w-1/2 ">
                  <Button
                    className="w-full rounded-md border-r border-gray-200"
                    variant="secondary"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="w-1/2">
                  <Button
                    className="w-full rounded-md bg-[#181818] hover:bg-[#181818]/40 !text-white font-medium"
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => {
                      updateSubUserLicense(subscriptionId);
                    }}
                  >
                    {isLoading ? "Processing..." : "Yes, Update"}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            modal={false}
            open={isAddSubDialogOpen}
            onOpenChange={(open) => {
              setIsAddSubDialogOpen(open);
              if (!open) {
                setQuantities({});
              }
            }}
          >
            {isAddSubDialogOpen && (
              <section className="fixed inset-0 bg-black/70 z-[50]">
                <DialogContent className="w-[60%] max-w-[900px] p-0 flex flex-col  rounded-xl overflow-hidden bg-white">
                  <DialogHeader className="bg-[#f4f4f4] text-[#202b49] px-6 py-4 ">
                    <DialogTitle className="text-xl font-semibold text-gray-700">
                      Add New Subscription
                    </DialogTitle>
                  </DialogHeader>

                  <div className="mt-2 w-[95%] mx-auto">
                    <div className="flex flex-col gap-y-2">
                      <p className="text-[14px] text-[#616161]">Recurrence</p>
                      <div className="flex w-1/2 border rounded overflow-hidden mb-1 h-[40px] ">
                        {tabs.map((tab, index) => (
                          <button
                            key={tab.key}
                            onClick={() => {
                              setActiveTab(tab.key);
                              setQuantities({});
                            }}
                            className={`flex-1 py-[10px] capitalize text-sm font-medium text-center border-r last:border-r-0 
    ${
      activeTab === tab.key
        ? "bg-green-600 text-white"
        : "bg-[#f3f3f3] text-black"
    }
    ${index === 0 ? "rounded-l" : ""}
    ${index === tabs.length - 1 ? "rounded-r" : ""}
  `}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Table shown only when 1 month is selected */}

                    {groupedCatalogueMap[activeTab] && (
                      <div className="mt-4 mr-2">
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="text-left px-4 py-2 border-b">
                                  Plan
                                </th>
                                <th className="text-left px-4 py-2 border-b">
                                  Quantity
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupedCatalogueMap[activeTab].prices.map(
                                (price) => (
                                  <tr key={price.price_id} className="border-t">
                                    <td className="px-4 py-2">
                                      <div className="flex items-center">
                                        {price.name}
                                        <Dot size={30} />
                                        <div className="text-[#828282] ">
                                          <span className=" ">$</span>
                                          <span className=" leading-[1.3] ml-1">
                                            {price.price}{" "}
                                          </span>
                                          <span className=" leading-[1.3] uppercase ">
                                            {price.currency}{" "}
                                          </span>
                                          <span className="text-[16px] ">
                                            / {price.intervalCount}{" "}
                                            {price.intervalCount > 1
                                              ? `${price.interval}s`
                                              : `${price.interval}`}
                                          </span>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2">
                                      <div className="flex items-center h-[32px] mt-1 border border-[#e4e4e4] bg-white rounded-[8px] w-[120px]">
                                        <button
                                          className="w-[40px] h-full flex items-center justify-center text-md font-bold bg-[#828282] bg-opacity-[0.08]"
                                          onClick={() =>
                                            handleQuantityChange1(
                                              price.price_id,
                                              (quantities[price.price_id] ||
                                                0) - 1
                                            )
                                          }
                                        >
                                          -
                                        </button>
                                        <span className="text-md font-medium flex-1 text-center text-gray-800">
                                          {quantities[price.price_id] || 0}
                                        </span>
                                        <button
                                          className="w-[40px] h-full flex items-center justify-center text-md font-bold bg-[#828282] bg-opacity-[0.08]"
                                          onClick={() =>
                                            handleQuantityChange1(
                                              price.price_id,
                                              (quantities[price.price_id] ||
                                                0) + 1
                                            )
                                          }
                                        >
                                          +
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 mb-2">
                      <div className="flex justify-end mr-3 ">
                        <p className="text-[#828282]">
                          Sub Total:{" "}
                          <span className="font-medium text-black ml-4">
                            ${calculateSubtotal().toFixed(2)}
                          </span>
                        </p>
                      </div>

                      <hr className="border-t border-gray-200 mb-4" />
                      <div className="flex justify-end space-x-4">
                        <Button
                          className="px-6 py-1 h-[40px] rounded"
                          variant="secondary"
                          onClick={() => {
                            setIsAddSubDialogOpen(false);
                            setQuantities({});
                          }}
                        >
                          Cancel
                        </Button>

                        <Button
                          className="bg-[#181818] px-6 py-1 h-[40px] disabled:bg-gray-600 rounded size-sm"
                          onClick={onSubmit}
                          disabled={
                            !Object.values(quantities).some(
                              (value) => value != undefined && value > 0
                            )
                          }
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </section>
            )}
          </Dialog>
        </div>
      )}
    </div>
  );
};

export default Subscription;
